/**
 * The complete lot number — format, parse, and segment-wise mutation.
 *
 *     {PlantCode}{YearCode}{LotSeq} / {Serial} / {Batch}{StageCode}
 *             DY09683              /  00000   /  00      R
 *
 * Three segments, three lifetimes (SeedTrac's Lot Number Discipline,
 * docs/reference/lot-number-discipline.md):
 *  - identifier (DY09683): PERMANENT. Lotgen issues Y09683 (year code + 5 digits), the plant code
 *    is added when it reaches a plant, and it is opaque thereafter. A blend made at a plant mints
 *    a 9-series identifier instead (DY90001).
 *  - serial (00000): set only by a sales return, to the receiving plant's running sales-return
 *    number for the year (the 3rd return of the year → 00003).
 *  - batch + stage code (00R): batch bumped only on a PARTIAL pick (processing, reprocessing,
 *    partial packing); stage code re-stamped on every movement.
 *
 * Legacy tolerance: opening stock may carry a BARE lot (Y09683). Every function degrades to
 * "return it unchanged" on such a value rather than throwing, exactly as vnr-prod did.
 */

export const SEQ_PAD = 5;
export const SERIAL_PAD = 5;
export const BATCH_PAD = 2;
export const SEPARATOR = "/";

/**
 * The bare lot seedtrac's tablet accepts on a PDN arrival: one letter, then exactly five digits
 * (Y26921). The letter is the lot's YEAR CODE, so which letters are valid is the Year Codes
 * master's call (user, 2026-10-01) -- this checks the shape only.
 */
const SEEDTRAC_LOT_RE = /^[A-Z]\d{5}$/i;

export function isSeedtracLotNumber(value: string | null | undefined): boolean {
  return SEEDTRAC_LOT_RE.test((value ?? "").trim());
}

/** The year-code letter a seedtrac lot leads with (Y26921 -> "Y"), or null when it isn't one. */
export function seedtracYearLetter(
  value: string | null | undefined
): string | null {
  const v = (value ?? "").trim().toUpperCase();
  return SEEDTRAC_LOT_RE.test(v) ? v[0]! : null;
}

// identifier is opaque ([^/]+); batch is greedy digits and stage code letters-only, which is what
// keeps the last segment unambiguous ("00R" = batch 00 + stage R, "001" = batch 001 + no stage).
// A numeric stage code would reintroduce the ambiguity, so stage codes are validated alphabetic.
const LOT_RE = new RegExp(
  `^(?<identifier>[^/]+)/(?<serial>\\d{${SERIAL_PAD},})/(?<batch>\\d{${BATCH_PAD},})(?<stageCode>[A-Za-z]*)$`
);

export interface LotParts {
  identifier: string;
  serial: number;
  batch: number;
  stageCode: string;
}

const zfill = (n: number, width: number) =>
  String(Math.trunc(n)).padStart(width, "0");

/** Compose the complete lot number. Returns "" for a blank identifier (= "not minted yet"). */
export function formatLotNumber(
  identifier: string,
  serial = 0,
  batch = 0,
  stageCode = ""
): string {
  const ident = (identifier ?? "").trim();
  if (!ident) return "";
  const code = (stageCode ?? "").trim().toUpperCase();
  return `${ident}${SEPARATOR}${zfill(serial || 0, SERIAL_PAD)}${SEPARATOR}${zfill(batch || 0, BATCH_PAD)}${code}`;
}

/** Parts of a complete lot number, or null for a legacy bare lot / blank / malformed value. */
export function parseLotNumber(
  value: string | null | undefined
): LotParts | null {
  if (value === null || value === undefined) return null;
  const m = LOT_RE.exec(String(value).trim());
  if (!m?.groups) return null;
  return {
    identifier: m.groups.identifier!,
    serial: Number.parseInt(m.groups.serial!, 10),
    batch: Number.parseInt(m.groups.batch!, 10),
    stageCode: m.groups.stageCode!.toUpperCase(),
  };
}

export function isCompleteFormat(value: string | null | undefined): boolean {
  return parseLotNumber(value) !== null;
}

/** The PERMANENT identity segment. A legacy bare lot is its own identifier; blank → "". */
export function identifierOf(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const parts = parseLotNumber(value);
  return parts ? parts.identifier : String(value).trim();
}

function mutate(
  value: string,
  patch: { serial?: number; batch?: number; stageCode?: string }
): string {
  const parts = parseLotNumber(value);
  if (!parts) return value; // legacy degrade — never rewrite a string other tables join on
  return formatLotNumber(
    parts.identifier,
    patch.serial ?? parts.serial,
    patch.batch ?? parts.batch,
    patch.stageCode ?? parts.stageCode
  );
}

/** COMPLETE movement: keep batch, re-stamp the stage letter (…/00R → …/00C). */
export function withStageCode(
  value: string,
  stageCode: string | null | undefined
): string {
  return mutate(value, { stageCode: stageCode ?? "" });
}

/** PARTIAL movement: the moved portion earns its own batch (…/00R → …/01C). */
export function withBatch(
  value: string,
  batch: number,
  stageCode?: string
): string {
  return mutate(
    value,
    stageCode === undefined ? { batch } : { batch, stageCode }
  );
}

/** Customer return: bump serial and RESET batch (DY09683/00000/02C → DY09683/00001/00C). */
export function withSerial(
  value: string,
  serial: number,
  stageCode?: string
): string {
  return mutate(
    value,
    stageCode === undefined
      ? { serial, batch: 0 }
      : { serial, batch: 0, stageCode }
  );
}

/**
 * Identifier for a lot minted at this plant: plant code + Core's digits (PDN/S-Capture), or
 * plant code + year code + zero-padded sequence (trading arrival).
 */
export function buildIdentifier(
  plantCode: string,
  coreLotNumber: string
): string;
export function buildIdentifier(
  plantCode: string,
  yearCode: string,
  sequence: number
): string;
export function buildIdentifier(
  plantCode: string,
  a: string,
  sequence?: number
): string {
  const plant = (plantCode ?? "").trim().toUpperCase();
  if (!plant)
    throw new Error("plant code is required to build a lot identifier");
  if (sequence === undefined) return `${plant}${a.trim()}`;
  return `${plant}${a.trim().toUpperCase()}${zfill(sequence, SEQ_PAD)}`;
}

/** The stage letter for a lot, or "" — a stage configured without a code yields no letter. */
export function stageCodeOf(
  stage: { code?: string | null } | null | undefined
): string {
  return (stage?.code ?? "").trim().toUpperCase();
}

/**
 * Operator-facing lot string. A COMPLETE lot is returned untouched (it already carries its plant
 * code); a LEGACY bare lot gets `<plantCode><lot>`; a blank renders blank, never a bare plant code.
 */
export function buildDisplayLot(
  lotNumber: string | null | undefined,
  plantCode: string | null | undefined
): string {
  const lot =
    lotNumber === null || lotNumber === undefined ? "" : String(lotNumber);
  if (!lot.trim()) return "";
  if (isCompleteFormat(lot)) return lot;
  const code = (plantCode ?? "").trim().toUpperCase();
  return code ? `${code}${lot}` : lot;
}

/**
 * The Lot No. an arrival line shows on every screen and print (user, 2026-10-15): always the full
 * number with its plant code. A minted lot reads as itself (a bare legacy one gets its origin plant
 * code); a PDN / S-Capture / Lotgen line not yet submitted reads as the lot submit WILL mint —
 * plant code + Core's number + /00000/00 + stage, exactly what `allocateLotIdentifier` builds.
 */
export function arrivalLineLot(l: {
  outputLot?: string | null | undefined;
  outputOriginPlant?: string | null | undefined;
  inputLot?: string | null | undefined;
  inputOriginPlant?: string | null | undefined;
  provisional?: string | null | undefined;
  external?: string | null | undefined;
  lotSource?: string | null | undefined;
  /** The receiving plant's code (the arrival's facility). */
  plantCode?: string | null | undefined;
  stageCode?: string | null | undefined;
  /**
   * A Maize-Dried Seed Arrival line: submit mints plant code + the dryer lot's identifier, its
   * serial and batch (0 for a legacy bare one) + stage (`mintReceiptPlantLot`).
   */
  receiptOfDryerLot?: boolean | null | undefined;
  inputIdentifier?: string | null | undefined;
  inputSerial?: number | null | undefined;
  inputBatch?: number | null | undefined;
  inputLegacy?: boolean | null | undefined;
}): string | null {
  if (l.outputLot?.trim()) return buildDisplayLot(l.outputLot, l.outputOriginPlant);
  const receiptPlant = (l.plantCode ?? "").trim();
  if (l.receiptOfDryerLot && receiptPlant && l.inputIdentifier?.trim())
    return `${buildIdentifier(receiptPlant, l.inputIdentifier.trim().toUpperCase())}${SEPARATOR}${zfill(l.inputLegacy ? 0 : (l.inputSerial ?? 0), SERIAL_PAD)}${SEPARATOR}${zfill(l.inputLegacy ? 0 : (l.inputBatch ?? 0), BATCH_PAD)}${(l.stageCode ?? "").trim().toUpperCase()}`;
  const fed = l.lotSource === "pdn" || l.lotSource === "scapture" || l.lotSource === "lotgen";
  const core = (l.external ?? l.provisional ?? "").trim().toUpperCase();
  const plant = (l.plantCode ?? "").trim();
  if (fed && core && plant && !isCompleteFormat(core))
    return `${buildIdentifier(plant, core)}${SEPARATOR}${zfill(0, SERIAL_PAD)}${SEPARATOR}${zfill(0, BATCH_PAD)}${(l.stageCode ?? "").trim().toUpperCase()}`;
  if (l.provisional?.trim()) return l.provisional;
  if (l.inputLot?.trim()) return buildDisplayLot(l.inputLot, l.inputOriginPlant);
  return l.external?.trim() ? l.external : null;
}

/**
 * Terms to OR-match an EXTERNAL (bare, Core-format) lot search against, tolerant of a typed
 * display prefix: "BY90089" also tries "Y90089" when "B" is a known plant code. Longest code wins.
 */
export function lotSearchTerms(
  term: string | null | undefined,
  plantCodes: Iterable<string>
): string[] {
  const t = (term ?? "").trim();
  if (!t) return [];
  const terms = [t];
  const upper = t.toUpperCase();
  const codes = [...plantCodes]
    .map((c) => c.trim().toUpperCase())
    .filter(Boolean);
  for (const code of codes.sort((a, b) => b.length - a.length)) {
    if (upper.startsWith(code) && t.length > code.length) {
      const stripped = t.slice(code.length);
      if (stripped && !terms.includes(stripped)) terms.push(stripped);
      break;
    }
  }
  return terms;
}
